/**
 * Dashboard Store
 *
 * Manages dashboard layout data, tile content, and visual styling.
 * The 'content' field is an array of DashboardTile objects that combine
 * react-grid-layout data with tile content information. Actual tile content
 * pieces (images and interactive content) are managed by sqReportStore.
 */

import _ from 'lodash';
import { useEffect } from 'react';
import { HEADER_HEIGHT } from '@/dashboard/dashboard.types';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type GetStoreState, type SetStoreState, createStore } from '@seeqdev/utils';
import type {
  DashboardBackgroundStyle,
  DashboardContent,
  DashboardTile,
  TileOverlapStatus,
} from '@/dashboard/dashboard.types';

export interface DashboardState {
  /** Number of columns in the dashboard grid */
  columnCount: number;
  /** Array of dashboard tiles with their layout and content information */
  content: DashboardTile[];
  /** Background style applied to the entire dashboard page */
  pageBackgroundStyle: DashboardBackgroundStyle;
  /** Default background style applied to new tiles */
  defaultTileBackgroundStyle: DashboardBackgroundStyle | null;
  /** Position where a tile is pending insertion, or null if no pending insert */
  pendingInsertPosition: { x: number; y: number } | null;
  /** Tile overlap behavior: 'overlap' allows tiles to overlap, 'auto-push' automatically pushes tiles down */
  overlapStatus: TileOverlapStatus;
}

const initialState: DashboardState = {
  columnCount: 12,
  content: [],
  pageBackgroundStyle: { mode: 'none' },
  defaultTileBackgroundStyle: null,
  pendingInsertPosition: null,
  overlapStatus: 'overlap',
};

const dashboardActionsFactory = (
  set: SetStoreState<DashboardState>,
  _get: GetStoreState<DashboardState>,
  resetState: (overrides?: Partial<DashboardState>) => void,
) => ({
  /**
   * Merge layout changes from react-grid-layout onto existing content.
   * react-grid-layout strips custom properties (title, id, type), so this
   * merges only the layout changes while preserving tile metadata.
   */
  mergeLayoutChanges: (layout: ReactGridLayout.Layout[]) =>
    set((draft: DashboardState) => {
      draft.content = layout.map((gridItem) => {
        const existingItem = draft.content.find((item) => item.id === gridItem.i);
        return { ...existingItem, ...gridItem } as DashboardTile;
      });
    }),

  /**
   * Add a tile to the dashboard. If it's a heading at y=0 in overlap mode,
   * shifts existing content down to make room.
   */
  addTile: (tile: DashboardTile, heading = false) =>
    set((draft: DashboardState) => {
      let updatedContent = draft.content;

      // In overlap mode we must manually shift content down when a header is placed at y=0.
      // In auto-push mode the grid resolves the collision for us, so skip the manual shift.
      if (heading && tile.y === 0 && draft.overlapStatus === 'overlap') {
        updatedContent = updatedContent.map((item: DashboardTile) => ({
          ...item,
          y: item.y + HEADER_HEIGHT,
        }));
      }

      const tileWithBackground = {
        ...tile,
        backgroundStyle: tile.backgroundStyle
          ? _.cloneDeep(tile.backgroundStyle)
          : _.cloneDeep(draft.defaultTileBackgroundStyle),
      };

      draft.content = [...updatedContent, tileWithBackground];
    }),

  /** Update a specific property of a tile */
  updateTile: (id: string, property: string, value: unknown) =>
    set((draft: DashboardState) => {
      const tileIndex = draft.content.findIndex((item: DashboardContent) => item.id === id);
      if (tileIndex !== -1) {
        draft.content[tileIndex] = { ...draft.content[tileIndex], [property]: value };
      }
    }),

  /** Update the background style for a specific tile */
  updateTileBackground: (id: string, background: DashboardBackgroundStyle | null) =>
    set((draft: DashboardState) => {
      const tileIndex = draft.content.findIndex((item) => item.id === id);
      if (tileIndex !== -1) {
        draft.content[tileIndex] = {
          ...draft.content[tileIndex],
          backgroundStyle: _.cloneDeep(background),
        };
      }
    }),

  /** Apply a background style to all tiles and set it as the default */
  applyBackgroundToAllTiles: (background: DashboardBackgroundStyle | null) =>
    set((draft: DashboardState) => {
      draft.content = draft.content.map((item: DashboardTile) => ({
        ...item,
        backgroundStyle: _.cloneDeep(background),
      }));
      draft.defaultTileBackgroundStyle = _.cloneDeep(background);
    }),

  /** Set the default background style for new tiles */
  setDefaultTileBackground: (background: DashboardBackgroundStyle | null) =>
    set((draft: DashboardState) => {
      draft.defaultTileBackgroundStyle = _.cloneDeep(background);
    }),

  /** Update the page background style */
  updatePageBackground: (background: DashboardBackgroundStyle | null) =>
    set((draft: DashboardState) => {
      draft.pageBackgroundStyle = _.cloneDeep(background) ?? { mode: 'none' };
    }),

  /** Set the position where a tile insertion is pending */
  setPendingInsertPosition: (x: number, y: number) =>
    set((draft: DashboardState) => {
      draft.pendingInsertPosition = { x, y };
    }),

  /** Clear the pending tile insertion position */
  clearPendingInsertPosition: () =>
    set((draft: DashboardState) => {
      draft.pendingInsertPosition = null;
    }),

  /** Set the tile overlap behavior mode */
  setOverlapStatus: (overlapStatus: TileOverlapStatus) =>
    set((draft: DashboardState) => {
      draft.overlapStatus = overlapStatus;
    }),

  /** Remove a tile from the dashboard by ID */
  removeContent: (id: string) =>
    set((draft: DashboardState) => {
      draft.content = draft.content.filter((item) => item.id !== id);
    }),

  reset: resetState,
});

const [DashboardStoreProvider, useDashboardStoreInstance, useDashboardSelector] = createStore(
  'Dashboard',
  initialState,
  dashboardActionsFactory,
);

export { DashboardStoreProvider };

export const useContent = () => useDashboardSelector((state) => state.content);
export const useColumnCount = () => useDashboardSelector((state) => state.columnCount);
export const usePageBackgroundStyle = () => useDashboardSelector((state) => state.pageBackgroundStyle);
export const useDefaultTileBackgroundStyle = () => useDashboardSelector((state) => state.defaultTileBackgroundStyle);
export const usePendingInsertPosition = () => useDashboardSelector((state) => state.pendingInsertPosition);
export const useOverlapStatus = () => useDashboardSelector((state) => state.overlapStatus);

export const useDashboardActions = () => useDashboardStoreInstance().actions;

/** Excludes `pendingInsertPosition`, which is transient UI state that should not be persisted with the workstep. */
export type DashboardDehydratedState = Omit<DashboardState, 'pendingInsertPosition'>;

export const dehydrateDashboardState = (state: DashboardState): DashboardDehydratedState => {
  const { pendingInsertPosition, ...persistedState } = state;
  return persistedState;
};

// Persistence hook
export const usePersistDashboardStore = () => {
  const register = useRegisterPersistedStore();
  const useDashboardStore = useDashboardStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqDashboardStore',
      persistenceLevel: 'WORKSHEET',
      store: useDashboardStore,
      dehydrate: dehydrateDashboardState,
    });
  }, [register, useDashboardStore]);
};
